import { FirebaseServices } from "./config";
import { checkEntityLinks } from "./share-functions";
import { CustomAlert } from "@/components/ThemedAlert";

/**
 * Options for entity deletion confirmation flow.
 */
export interface IConfirmEntityDeletionOptions {
  services: FirebaseServices;
  entityType: "memory" | "media" | "group" | "contact" | "place" | "anchor";
  entityId: string;
  entityName?: string;
  onProceedDelete: (cascadeUnlink: boolean) => Promise<{ status?: boolean; error?: string } | void>;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

/**
 * Checks whether an entity has active linked references across collections and displays
 * an informative confirmation dialog. If references exist, prompts the user with
 * Option B UX ("Remove Links & Delete" vs "Cancel"). If no links exist, prompts standard deletion.
 *
 * @param {IConfirmEntityDeletionOptions} options - Configuration for the entity deletion confirmation.
 * @returns {Promise<void>} Resolves when the alert is triggered.
 */
export async function confirmEntityDeletion(options: IConfirmEntityDeletionOptions): Promise<void> {
  const { services, entityType, entityId, entityName, onProceedDelete, onSuccess, onError } = options;

  const entityTypeLabels: Record<string, string> = {
    memory: "Memory",
    media: "Media item",
    group: "Group",
    contact: "Contact",
    place: "Place",
    anchor: "Anchor",
  };
  const label = entityTypeLabels[entityType] || entityType;

  try {
    const linkCheck = await checkEntityLinks(services, { entityType, entityId });

    if (linkCheck && linkCheck.hasLinks) {
      CustomAlert.alert(
        "Item Has Linked References",
        `${linkCheck.summaryText}\n\nProceeding will remove all links and permanently delete this ${label.toLowerCase()}.`,
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Remove Links & Delete",
            style: "destructive",
            onPress: async () => {
              try {
                const res = await onProceedDelete(true);
                if (res && res.status === false) {
                  CustomAlert.alert("Error", res.error || `Failed to delete ${label.toLowerCase()}.`);
                  onError?.(res.error);
                } else {
                  onSuccess?.();
                }
              } catch (err: any) {
                CustomAlert.alert("Error", err?.message || `Failed to delete ${label.toLowerCase()}.`);
                onError?.(err);
              }
            },
          },
        ]
      );
    } else {
      // No links found -> standard delete confirmation
      const itemDescription = entityName ? `"${entityName}"` : `this ${label.toLowerCase()}`;
      CustomAlert.alert(
        `Delete ${label}`,
        `Are you sure you want to delete ${itemDescription}? This action cannot be undone.`,
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Delete",
            style: "destructive",
            onPress: async () => {
              try {
                const res = await onProceedDelete(false);
                if (res && res.status === false) {
                  CustomAlert.alert("Error", res.error || `Failed to delete ${label.toLowerCase()}.`);
                  onError?.(res.error);
                } else {
                  onSuccess?.();
                }
              } catch (err: any) {
                CustomAlert.alert("Error", err?.message || `Failed to delete ${label.toLowerCase()}.`);
                onError?.(err);
              }
            },
          },
        ]
      );
    }
  } catch (error: any) {
    // If link check itself fails, fallback to standard delete prompt with fail-safe notification
    console.warn("[confirmEntityDeletion] Link check failed:", error);
    const itemDescription = entityName ? `"${entityName}"` : `this ${label.toLowerCase()}`;
    CustomAlert.alert(
      `Delete ${label}`,
      `Are you sure you want to delete ${itemDescription}? This action cannot be undone.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              const res = await onProceedDelete(true);
              if (res && res.status === false) {
                CustomAlert.alert("Error", res.error || `Failed to delete ${label.toLowerCase()}.`);
                onError?.(res.error);
              } else {
                onSuccess?.();
              }
            } catch (err: any) {
              CustomAlert.alert("Error", err?.message || `Failed to delete ${label.toLowerCase()}.`);
              onError?.(err);
            }
          },
        },
      ]
    );
  }
}
